// /work-orders: every run of drones, and the inbox of runs a confirmed sale
// suggested. Both instances, identical.
//
// One fetch of the list feeds the Open, Draft, Done and All tabs (filtered here
// by status); the Inbox is its own route because its rows carry the suggestion
// maths. The inbox count is fetched on mount whatever tab is showing, since the
// badge is the reason to look at it.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import PageHeader, { PrimaryAction } from '../components/PageHeader';
import PageLayout from '../components/ui/PageLayout';
import { FilterBar, FilterEnd, FilterSearch, Segmented } from '../components/FilterBar';
import { DataTable } from '../components/table/DataTable';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import { formatEventDate, formatTimestamp } from '../utils/date-utils';
import { variantLabel } from '../Parts/bomsApi';
import { buttonClass } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import Field from '../components/ui/Field';
import { Skeleton } from '../components/ui/Skeleton';
import FadeIn from '../components/ui/FadeIn';
import Tag from '../components/ui/Tag';
import Tooltip from '../components/ui/Tooltip';
import NewWorkOrderDrawer from './NewWorkOrderDrawer';
import DismissDialog from './DismissDialog';
import Popover from './Popover';
import { BuiltCount, StatusChip } from './WorkOrderBits';
import {
  DONE_STATUSES,
  OPEN_STATUSES,
  useWorkOrdersApi,
  workOrderErrorMessage,
  type InboxRow,
  type WorkOrderRow,
  type WorkOrderStatus,
} from './workOrdersApi';

type Tab = 'open' | 'inbox' | 'draft' | 'done' | 'all';

const TABS: { key: Tab; label: string; hint: string }[] = [
  { key: 'open', label: 'Open', hint: 'Released, being built, or on hold' },
  { key: 'inbox', label: 'Inbox', hint: 'Suggested by a confirmed sale, waiting for approval' },
  { key: 'draft', label: 'Draft', hint: 'Approved, waiting to be released to the floor' },
  { key: 'done', label: 'Done', hint: 'Every drone built, or closed' },
  { key: 'all', label: 'All', hint: 'Every work order, cancelled ones included' },
];

const TAB_STATUSES: Record<Exclude<Tab, 'inbox'>, WorkOrderStatus[] | null> = {
  open: OPEN_STATUSES,
  draft: ['draft'],
  done: DONE_STATUSES,
  all: null,
};

const EMPTY: Record<Tab, { title: string; next: string }> = {
  open: {
    title: 'Nothing is being built right now.',
    next: 'Release a draft, approve a suggestion from the Inbox, or start one with New work order.',
  },
  inbox: {
    title: 'Nothing is waiting for approval.',
    next: 'When a confirmed sale needs drones the shelf does not have, its work order lands here.',
  },
  draft: {
    title: 'Nothing is waiting to be released.',
    next: 'An approved work order sits here until someone releases it to the floor.',
  },
  done: {
    title: 'No finished runs yet.',
    next: 'A run lands here when its last drone is built.',
  },
  all: {
    title: 'No work orders yet.',
    next: 'Start one with New work order, or confirm a sale that needs drones built.',
  },
};

// ── Cells ────────────────────────────────────────────────────────────────────

const stop = (e: React.SyntheticEvent) => e.stopPropagation();

const LINK =
  'rounded hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50';

// Factories, generic over the row, so the list and the inbox share one cell
// each without casting between two ColumnDef types tanstack holds invariant.
// Called once at module scope, so every column is still referentially stable.
const numberColumn = <T extends WorkOrderRow>(): ColumnDef<T, any> => ({
  id: 'number',
  header: 'Number',
  accessorFn: (o: T) => o.number,
  meta: { cellClass: 'whitespace-nowrap' },
  cell: ({ row }) => (
    <Link to={`/work-orders/${row.original.id}`} onClick={stop} className={`font-bold text-on-surface ${LINK}`}>
      {row.original.number}
    </Link>
  ),
});

const modelColumn = <T extends WorkOrderRow>(): ColumnDef<T, any> => ({
  id: 'model',
  header: 'Product',
  accessorFn: (o: T) => o.model_code ?? o.model_name,
  meta: { cellClass: 'max-w-[18rem]' },
  cell: ({ row }) => (
    <div className="min-w-0">
      <div className="font-medium text-on-surface">{row.original.model_code ?? row.original.model_name}</div>
      {row.original.model_code && row.original.model_name && (
        <div className="truncate text-xs text-on-surface-variant">{row.original.model_name}</div>
      )}
    </div>
  ),
});

const variantColumn = <T extends WorkOrderRow>(): ColumnDef<T, any> => ({
  id: 'variant',
  header: 'Build',
  accessorFn: (o: T) => o.variant ?? undefined,
  meta: { cellClass: 'whitespace-nowrap' },
  cell: ({ row }) =>
    row.original.variant ? (
      <span className="text-on-surface">{variantLabel(row.original.variant)}</span>
    ) : (
      <span className="text-on-surface-variant">—</span>
    ),
});

const plannedColumn = <T extends WorkOrderRow>(): ColumnDef<T, any> => ({
  id: 'planned',
  header: 'Planned',
  accessorFn: (o: T) => o.planned_date ?? undefined,
  meta: { cellClass: 'whitespace-nowrap tabular-nums', csv: (o: T) => o.planned_date ?? '' },
  cell: ({ row }) =>
    row.original.planned_date ? (
      formatEventDate(row.original.planned_date)
    ) : (
      <span className="text-on-surface-variant">—</span>
    ),
});

const SalesOrderLink: React.FC<{ o: WorkOrderRow }> = ({ o }) =>
  o.sales_order_id != null ? (
    <Link to={`/orders/${o.sales_order_id}`} onClick={stop} className={`text-on-surface ${LINK}`}>
      {o.so_number || 'Sales order'}
    </Link>
  ) : (
    <span className="text-on-surface-variant">Raised by hand</span>
  );

const LIST_COLUMNS: ColumnDef<WorkOrderRow, any>[] = [
  numberColumn<WorkOrderRow>(),
  modelColumn<WorkOrderRow>(),
  variantColumn<WorkOrderRow>(),
  {
    id: 'built',
    header: 'Built',
    // Sorts by how much is left, so the runs furthest from done rise first.
    accessorFn: (o: WorkOrderRow) => o.quantity - o.completed_count,
    meta: { csv: (o: WorkOrderRow) => `${o.completed_count} of ${o.quantity}` },
    cell: ({ row }) => <BuiltCount done={row.original.completed_count} total={row.original.quantity} />,
  },
  {
    id: 'status',
    header: 'Status',
    accessorFn: (o: WorkOrderRow) => o.status,
    cell: ({ row }) => (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
        <StatusChip status={row.original.status} />
        {!!row.original.open_pick_requests && (
          <Tooltip label={`${row.original.open_pick_requests} ${row.original.open_pick_requests === 1 ? 'part' : 'parts'} asked for from the line`}>
            <Tag size="sm" tone="accent">
              Box requested
            </Tag>
          </Tooltip>
        )}
      </span>
    ),
  },
  plannedColumn<WorkOrderRow>(),
  {
    id: 'source',
    header: 'Source',
    accessorFn: (o: WorkOrderRow) => o.so_number ?? undefined,
    meta: { cellClass: 'whitespace-nowrap' },
    cell: ({ row }) => <SalesOrderLink o={row.original} />,
  },
  {
    id: 'created',
    header: 'Created',
    accessorFn: (o: WorkOrderRow) => o.created_at,
    meta: { cellClass: 'whitespace-nowrap tabular-nums text-on-surface-variant' },
    cell: ({ row }) => formatTimestamp(row.original.created_at, 'MMM D, YYYY'),
  },
];

// ── Inbox ────────────────────────────────────────────────────────────────────

interface InboxMeta {
  canApprove: boolean;
  canEdit: boolean;
  busyId: string | null;
  onApprove: (o: InboxRow) => void;
  onQuantity: (o: InboxRow, qty: number) => Promise<boolean>;
  onDismiss: (o: InboxRow, reason: string) => Promise<boolean>;
}

/** Why the inbox suggested this many: the sale, minus what the shelf has. */
function suggestionReading(o: InboxRow): string | null {
  const s = o.suggestion;
  if (!s || s.line_quantity == null) return null;
  const free = Math.max(0, (s.available ?? 0) - (s.committed ?? 0));
  const parts = [`Sale wants ${s.line_quantity}`];
  if (free > 0) parts.push(`${free} on the shelf`);
  if (s.covered) parts.push(`${s.covered} already planned`);
  return parts.join(', ');
}

const QuantityForm: React.FC<{ initial: number; onSave: (n: number) => Promise<boolean>; onCancel: () => void }> = ({
  initial,
  onSave,
  onCancel,
}) => {
  const [text, setText] = useState(String(initial));
  const [busy, setBusy] = useState(false);
  const n = Number(text);
  const valid = Number.isInteger(n) && n > 0;
  return (
    <form
      className="space-y-2"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!valid) return;
        setBusy(true);
        const ok = await onSave(n);
        setBusy(false);
        if (ok) onCancel();
      }}
    >
      <label className="block">
        <span className="mb-1 block text-xs text-on-surface-variant">How many drones to build</span>
        <Field
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={text}
          onChange={(e) => setText(e.target.value.replace(/[^0-9]/g, ''))}
          onFocus={(e) => e.target.select()}
          className="tabular-nums"
        />
      </label>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className={buttonClass('quiet', 'md')}>
          Never mind
        </button>
        <button
          type="submit"
          disabled={!valid || busy}
          className={buttonClass('filled', 'md')}
        >
          {busy ? 'Saving…' : 'Save quantity'}
        </button>
      </div>
    </form>
  );
};

const INBOX_COLUMNS: ColumnDef<InboxRow, any>[] = [
  numberColumn<InboxRow>(),
  modelColumn<InboxRow>(),
  variantColumn<InboxRow>(),
  {
    id: 'quantity',
    header: 'To build',
    accessorFn: (o: InboxRow) => o.quantity,
    meta: { align: 'right', numeric: true },
    cell: ({ row }) => <span className="font-semibold text-on-surface">{row.original.quantity}</span>,
  },
  {
    id: 'why',
    header: 'Why',
    accessorFn: (o: InboxRow) => o.so_number ?? undefined,
    meta: { cellClass: 'max-w-[22rem]' },
    cell: ({ row }) => {
      const o = row.original;
      const reading = suggestionReading(o);
      return (
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <SalesOrderLink o={o} />
            {o.sales_order?.customer_name && (
              <span className="truncate text-xs text-on-surface-variant">{o.sales_order.customer_name}</span>
            )}
          </div>
          {reading && <div className="truncate text-xs text-on-surface-variant">{reading}</div>}
        </div>
      );
    },
  },
  plannedColumn<InboxRow>(),
  {
    id: 'actions',
    header: '',
    meta: { align: 'right', cellClass: 'whitespace-nowrap' },
    cell: ({ row, table }) => {
      const o = row.original;
      const m = table.options.meta as InboxMeta;
      const busy = m.busyId === o.id;
      return (
        <div className="flex items-center justify-end gap-1.5" onClick={stop}>
          {m.canApprove && (
            <Tooltip label="Approve the run. It waits in Draft until someone releases it.">
              <button type="button" disabled={busy} onClick={() => m.onApprove(o)} className={buttonClass('quiet', 'sm')}>
                Approve
              </button>
            </Tooltip>
          )}
          {m.canEdit && (
            <Popover
              triggerLabel="Edit quantity"
              triggerClassName={buttonClass('quiet', 'sm')}
              tooltip="Change how many drones this run builds"
              triggerAriaLabel={`Edit quantity of ${o.number}`}
              title={`Quantity for ${o.number}`}
              align="right"
              width="sm"
              disabled={busy}
            >
              {(close) => <QuantityForm initial={o.quantity} onSave={(n) => m.onQuantity(o, n)} onCancel={close} />}
            </Popover>
          )}
          {m.canApprove && (
            <DismissDialog
              triggerClassName={buttonClass('danger-quiet', 'sm')}
              triggerAriaLabel={`Dismiss ${o.number}`}
              tooltip="Not needed. Cancels the suggestion with your reason."
              align="right"
              disabled={busy}
              onConfirm={(reason) => m.onDismiss(o, reason)}
            />
          )}
        </div>
      );
    },
  },
];

// ── Page ─────────────────────────────────────────────────────────────────────

const SKELETON = (
  <div className="space-y-2 py-8" aria-busy="true" aria-label="Loading work orders">
    {[0, 1, 2].map((i) => (
      <Skeleton key={i} className="h-10 w-full rounded" />
    ))}
  </div>
);

const getRowId = (o: WorkOrderRow) => o.id;

function matches(o: WorkOrderRow, q: string): boolean {
  if (!q) return true;
  const hay = [o.number, o.model_code, o.model_name, o.so_number].filter(Boolean).join(' ').toLowerCase();
  return hay.includes(q);
}

const WorkOrdersPage: React.FC = () => {
  const woApi = useWorkOrdersApi();
  const toast = useToast();
  const history = useHistory();
  const location = useLocation();
  const user = useUserStore((s) => s.user);
  const isAdmin = !!(user?.isadmin || user?.can_admin);
  const canWrite = isAdmin || !!user?.can_manufacturing;
  const canManage = isAdmin || !!user?.can_manager;

  // The URL owns the tab, so the bell's ?tab=inbox link, Back and refresh all land on it.
  const tab = useMemo<Tab>(() => {
    const t = new URLSearchParams(location.search).get('tab');
    return TABS.some((x) => x.key === t) ? (t as Tab) : 'open';
  }, [location.search]);
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<WorkOrderRow[]>([]);
  const [inbox, setInbox] = useState<InboxRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [inboxLoading, setInboxLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [inboxError, setInboxError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      setRows((await woApi.list()) || []);
      setListError(null);
    } catch (err) {
      setListError(workOrderErrorMessage(err, 'The work orders did not load.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInbox = useCallback(async () => {
    setInboxLoading(true);
    try {
      setInbox((await woApi.inbox()) || []);
      setInboxError(null);
    } catch (err) {
      // The badge is a convenience; the Inbox tab says so when it is the one showing.
      setInbox([]);
      setInboxError(workOrderErrorMessage(err, 'The inbox did not load.'));
    } finally {
      setInboxLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    loadInbox();
    // Once on mount; actions refresh it themselves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const q = search.trim().toLowerCase();
  const listRows = useMemo(() => {
    if (tab === 'inbox') return [];
    const statuses = TAB_STATUSES[tab];
    return rows.filter((o) => (!statuses || statuses.includes(o.status)) && matches(o, q));
  }, [rows, tab, q]);
  const inboxRows = useMemo(() => inbox.filter((o) => matches(o, q)), [inbox, q]);

  const afterInboxChange = (id: string) => {
    setInbox((prev) => prev.filter((o) => o.id !== id));
    loadList();
  };

  const onApprove = async (o: InboxRow) => {
    setBusyId(o.id);
    try {
      await woApi.approve(o.id);
      toast.success(`Approved ${o.number}. It is in Draft until someone releases it.`);
      afterInboxChange(o.id);
    } catch (err) {
      toast.error(workOrderErrorMessage(err, 'The approval did not save. Try again.'));
    } finally {
      setBusyId(null);
    }
  };

  const onQuantity = async (o: InboxRow, qty: number): Promise<boolean> => {
    try {
      const detail = await woApi.update(o.id, { quantity: qty });
      setInbox((prev) => prev.map((r) => (r.id === o.id ? { ...r, quantity: detail.order.quantity } : r)));
      toast.success(`${o.number} now builds ${detail.order.quantity}.`);
      return true;
    } catch (err) {
      toast.error(workOrderErrorMessage(err, 'The quantity did not save. Try again.'));
      return false;
    }
  };

  const onDismiss = async (o: InboxRow, reason: string): Promise<boolean> => {
    try {
      await woApi.cancel(o.id, reason);
      toast.success(`Dismissed ${o.number}.`);
      afterInboxChange(o.id);
      return true;
    } catch (err) {
      toast.error(workOrderErrorMessage(err, 'The suggestion was not dismissed. Try again.'));
      return false;
    }
  };

  const inboxMeta = useMemo<InboxMeta>(
    () => ({ canApprove: canManage, canEdit: canWrite, busyId, onApprove, onQuantity, onDismiss }),
    // The handlers close over setters and the api module only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canManage, canWrite, busyId],
  );

  const open = (o: WorkOrderRow) => history.push(`/work-orders/${o.id}`);
  const rowProps = (o: WorkOrderRow) => ({
    className: 'cursor-pointer',
    onClick: (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('a,button,input,textarea')) return;
      open(o);
    },
  });

  const empty = q ? (
    <EmptyState
      variant="area"
      action={
        <button type="button" onClick={() => setSearch('')} className={buttonClass('tonal', 'md')}>
          Clear the search
        </button>
      }
    >
      Nothing here matches “{search.trim()}”.
    </EmptyState>
  ) : (
    <EmptyState variant="area" title={EMPTY[tab].title}>
      {EMPTY[tab].next}
    </EmptyState>
  );

  const shown = tab === 'inbox' ? inboxRows.length : listRows.length;
  const error = tab === 'inbox' ? inboxError : listError;
  const retrying = tab === 'inbox' ? inboxLoading : loading;

  return (
    <PageLayout width="wide"
      kind="workspace"
      className="space-y-5 pt-4"
      header={
      <PageHeader
        title="Work orders"
        helpTitle="About work orders"
        lede="Runs of drones being built, from the parts on the shelf to the last one finished."
        help={
          <>
            <p>
              A work order is one run: a number of drones of one model, built from its parts list. It
              starts as a suggestion or by hand, gets approved, and is released to the floor.
            </p>
            <p className="font-medium text-on-surface">The inbox</p>
            <p>
              When a sale is confirmed and the shelf does not hold enough finished drones, a work order is
              suggested for the difference. A manager approves it, changes the quantity, or dismisses it with a
              reason. Approving does not start the build; releasing does.
            </p>
            <p className="font-medium text-on-surface">Picks</p>
            <p>
              Once approved, scan each box the run needs. The whole box moves to the Assembly area of its
              warehouse, and what is left over stays there for the next run. A run marked Box requested has a
              drone waiting at the station for a part; picking a box that holds it fills the request.
            </p>
            <p className="font-medium text-on-surface">Finishing drones</p>
            <p>
              Drones join and finish by scan, in Assembly mode on the scanner. A drone joins the run when its
              first part is scanned onto it and is built when its final check passes. A short part never
              finishes a drone: the station asks for another box, or a manager holds the run. Flag takes a
              drone that fails at the bench off the run, and its parts stay charged to it.
            </p>
            <p className="font-medium text-on-surface">Closed or cancelled</p>
            <p>
              Closed is the normal end of a run: it was built, fully or short, and its close report is the
              account. Cancelled means the run was called off with a reason, whether a dismissed suggestion or
              a stopped run; nothing already picked or built is undone, and there is no report. Closed runs
              sit under Done; cancelled ones show only under All.
            </p>
          </>
        }
        primary={
          canWrite ? <PrimaryAction onClick={() => setCreating(true)}>+ New work order</PrimaryAction> : undefined
        }
      />
      }
    >

      <FilterBar>
        <Segmented
          label="Which work orders to show"
          options={TABS.map((t) => ({
            key: t.key,
            label: t.label,
            badge: t.key === 'inbox' ? inbox.length : undefined,
            title: t.hint,
          }))}
          value={tab}
          onChange={(k) => {
            const params = new URLSearchParams(location.search);
            params.set('tab', k);
            history.replace({ pathname: location.pathname, search: `?${params.toString()}` });
          }}
        />
        <FilterSearch
          value={search}
          onChange={setSearch}
          label="Search work orders by number, model or sales order"
          placeholder="Search work orders…"
        />
        <FilterEnd>
          <span role="status">
            {!(tab === 'inbox' ? inboxLoading : loading) && `${shown} ${shown === 1 ? 'work order' : 'work orders'}`}
          </span>
        </FilterEnd>
      </FilterBar>

      <FadeIn key={error ? 'error' : tab}>
      {error ? (
        <Alert
          tone="error"
          action={
            <button
              type="button"
              disabled={retrying}
              onClick={() => (tab === 'inbox' ? loadInbox() : loadList())}
              className={buttonClass('tonal', 'md')}
            >
              {retrying ? 'Trying again…' : 'Try again'}
            </button>
          }
        >
          {error}
        </Alert>
      ) : tab === 'inbox' ? (
        <DataTable<InboxRow>
          data={inboxRows}
          columns={INBOX_COLUMNS}
          meta={inboxMeta}
          getRowId={getRowId}
          sortable
          initialSorting={[{ id: 'planned', desc: false }]}
          rowKeyboard={{ onOpen: open }}
          rowProps={rowProps}
          loading={inboxLoading}
          loadingContent={SKELETON}
          empty={empty}
        />
      ) : (
        <DataTable<WorkOrderRow>
          data={listRows}
          columns={LIST_COLUMNS}
          getRowId={getRowId}
          sortable
          initialSorting={[{ id: 'created', desc: true }]}
          rowKeyboard={{ onOpen: open }}
          rowProps={rowProps}
          loading={loading}
          loadingContent={SKELETON}
          empty={empty}
        />
      )}
      </FadeIn>

      {creating && <NewWorkOrderDrawer onClose={() => setCreating(false)} />}
    </PageLayout>
  );
};

export default WorkOrdersPage;
